import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { queryClient } from "..";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { deliveryState } from "../app/reducers/delivery-slice";
import { profileState } from "../app/reducers/profile-slice";
import { ContainedGrayButton } from "../components/button/ContainedGrayButton";
import { ContainedRedButton } from "../components/button/ContainedRedButton";
import { ContainedYellowButton } from "../components/button/ContainedYellowButton";
import DateRangePicker, {
    DateRangeState,
} from "../components/date/DateRangePicker";
import { DeleteDeliveryAllDialogContainer } from "../components/dialog/DeleteDeliveryAllDialogContainer";
import { DeliveryExcelUploadDialog } from "../components/dialog/DeliveryExcelUploadDialog";
import { DeliveryDialog } from "../components/dialog/delivery-dialog/DeliveryDialog";
import { PageController } from "../components/paging/PageController";
import { BasicSingleSelector } from "../components/selector/BasicSingleSelector";
import { DeliveryListTable } from "../components/table/DeliveryListTable";
import { IconTextField } from "../components/text-field/IconTextField";
import {
    DeliveryQueryKey,
    useGetDeliveryByIdQuery,
    useGetDeliveryPageByPeriodQuery,
    useDeleteDeliveriesMutation,
} from "../query/delivery-query";
import theme from "../style/theme";
import { useDebouncedCallback } from "../utils/custom-hook/use-debounced-callback";
import useDidMountEffect from "../utils/custom-hook/use-did-mount-effect";
import { dateFormat, isSameDay } from "../utils/date/date-format";
import { startCenterLoadingAction, startGlobalLoadingAction, endGlobalLoadingAction } from "../app/reducers/loading-slice";
import { Selector } from "../components/selector/Selector";
import { DeliveryStatusLabels } from "../types/delivery/delivery-status";
import { convertStatusesToEnumString } from "../utils/converter/delivery/delivery-status-converter";
import EventBus, { EventTypes } from "../utils/eventbus/event-bus";

const searchTabMap: { [key: string]: string } = {
    workerName: "프로님",
    clientName: "고객사",
    agencyName: "대행사",
    pickUpAddressKeyword: "상차지",
    dropOffAddressKeyword: "전달지",
};

const placeholderMap: { [key: string]: string } = {
    workerName: "프로님 이름을 입력하세요",
    clientName: "고객사명을 입력하세요",
    agencyName: "대행사명을 입력하세요",
    pickUpAddressKeyword: "상차지를 입력하세요",
    dropOffAddressKeyword: "전달지를 입력하세요",
};

const DeliveryView = () => {
    const dispatch = useAppDispatch();
    const { selectedDelivery, deliveryPage } = useAppSelector(deliveryState);
    const { profile } = useAppSelector(profileState);
    const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
    const [deleteDeliveryAllDialogOpen, setDeleteDeliveryAllDialogOpen] =
        useState(false);
    const [excelDialogOpen, setExcelDialogOpen] = useState(false);
    const [dateRange, setDateRange] = useState<DateRangeState[]>([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: "selection",
        },
    ]);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [selectedSearchTabStatus, setSelectedSearchTabStatus] =
        useState<string>("workerName");
    const [pagePayload, setPagePayload] = useState<any>({});
    const [idPayload, setIdPayload] = useState<string>("");
    const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
    const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<string[]>([]);
    const deleteDeliveriesMutation = useDeleteDeliveriesMutation();
    useGetDeliveryByIdQuery(idPayload);
    useGetDeliveryPageByPeriodQuery(pagePayload);

    // 날짜 범위가 다른 날인지 체크
    const isDifferentDates = dateRange[0]?.startDate && dateRange[0]?.endDate
        ? !isSameDay(dateRange[0].startDate, dateRange[0].endDate)
        : false;

    const handleClickStatus = async (key: string) => {
        setSelectedStatus((prevStatus) => {
            if (prevStatus.includes(key)) {
                return prevStatus.filter((v) => v !== key);
            } else {
                return [...prevStatus, key];
            }
        });
    };

    useDidMountEffect(() => {
        EventBus.emit(EventTypes.PAGE_CLEAR);
        setSearchPayload();
    }, [selectedStatus])

    useDidMountEffect(() => {
        queryClient.invalidateQueries(DeliveryQueryKey.Page);
    }, [pagePayload])

    const setSearchPayload = async (page: number = 0) => {
        dispatch(startCenterLoadingAction());
        const newPayload: any = {
            page,
            fromDate: dateFormat(dateRange[0].startDate),
            toDate: dateFormat(dateRange[0].endDate),
            status: convertStatusesToEnumString(selectedStatus),
        };
        if (selectedSearchTabStatus.length > 0 && searchKeyword !== "") {
            newPayload[selectedSearchTabStatus] = searchKeyword;
        }
        await setPagePayload(newPayload);
    };

    const debouncedSearchPayload = useDebouncedCallback(
        async (page: number = 0) => await setSearchPayload(page)
    );

    const handleChangePage = (event: { selected: number }) => {
        const page = event.selected;
        setSearchPayload(page);
    };

    const handleClickDeleteDeliveryAllDialogOpen = () => {
        setDeleteDeliveryAllDialogOpen(true);
    };

    const handleClickDeleteDeliveryAllDialogClose = () => {
        setDeleteDeliveryAllDialogOpen(false);
    };

    const handleClickDialogOpen = () => {
        setDeliveryDialogOpen(true);
    };

    const handleClickDialogClose = () => {
        setDeliveryDialogOpen(false);
    };

    const handleClickExcelDialogOpen = () => {
        setExcelDialogOpen(true);
    };

    const handleClickExcelDialogClose = () => {
        setExcelDialogOpen(false);
    };

    const handleChangeTextField = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newSearchKeyword = event.target.value;
        setSearchKeyword(newSearchKeyword);
    };

    const handleClickSearchTabItem = (key: string) => {
        setSelectedSearchTabStatus(key);
        setSearchKeyword("");
    };

    useDidMountEffect(() => {
        EventBus.emit(EventTypes.PAGE_CLEAR);
        debouncedSearchPayload();
    }, [searchKeyword]);

    useEffect(() => {
        EventBus.emit(EventTypes.PAGE_CLEAR);
        setSearchPayload();
    }, [dateRange]);

    useDidMountEffect(() => {
        if (selectedDelivery) {
            setDeliveryDialogOpen(true);
        } else {
            setDeliveryDialogOpen(false);
        }
    }, [selectedDelivery]);

    const handleDeleteSelected = async () => {
        if (window.confirm("선택한 배송을 모두 삭제하시겠습니까?")) {
            try {
                dispatch(startGlobalLoadingAction());
                await deleteDeliveriesMutation.mutateAsync(selectedDeliveryIds);
                setSelectedDeliveryIds([]);
            } catch (error) {
            } finally {
                dispatch(endGlobalLoadingAction());
            }
        }
    };

    return (
        <DeliveryViewContainer>
            <DeliveryViewTitleContainer>
                <DeliveryViewTitleLeftContainer>
                    물류관리
                </DeliveryViewTitleLeftContainer>
                <DeliveryViewTitleRightContainer>
                    {/*{profile?.company.name === "SUPERUSER" && (*/}
                    {/*  <ContainedRedButtonContainer>*/}
                    {/*    <ContainedRedButton*/}
                    {/*      width="8.188rem"*/}
                    {/*      height="3.188rem"*/}
                    {/*      text={"전체 삭제"}*/}
                    {/*      onClick={handleClickDeleteDeliveryAllDialogOpen}*/}
                    {/*      sx={{*/}
                    {/*        fontWeight: "500",*/}
                    {/*        fontSize: "1rem",*/}
                    {/*        lineHeight: "1.188rem",*/}
                    {/*      }}*/}
                    {/*    />*/}
                    {/*  </ContainedRedButtonContainer>*/}
                    {/*)}*/}
                    <ContainedGrayButtonContainer>
                        <ContainedGrayButton
                            width="8.188rem"
                            height="3.188rem"
                            text={"수기 등록"}
                            onClick={handleClickDialogOpen}
                        />
                    </ContainedGrayButtonContainer>
                    <ContainedYellowButton
                        width="8.188rem"
                        height="3.188rem"
                        text={"파일 등록"}
                        onClick={handleClickExcelDialogOpen}
                    />
                </DeliveryViewTitleRightContainer>
            </DeliveryViewTitleContainer>

            <DeliveryViewHeaderContainer>
                <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
                <Divider />
                <SelectorContainer>
                    <BasicSingleSelector
                        value={searchTabMap[selectedSearchTabStatus]}
                        defaultKey={"이름"}
                        candidateMap={searchTabMap}
                        onClickSelectItem={handleClickSearchTabItem}
                        width="7.063rem"
                    />
                </SelectorContainer>
                <IconTextField
                    placeholder={placeholderMap[selectedSearchTabStatus]}
                    name={selectedSearchTabStatus}
                    value={searchKeyword}
                    onChange={handleChangeTextField}
                    width="300px"
                />
                <MultiSelectorContainer>
                    <Selector
                        keys={selectedStatus}
                        defaultKey={""}
                        candidateMap={DeliveryStatusLabels}
                        onClickFilterMultiSelectItem={handleClickStatus}
                        width="15rem"
                        placeholder="배송상태"
                    />
                </MultiSelectorContainer>
                {selectedDeliveryIds.length > 0 && (
                    <DeleteButtonContainer>
                        <ContainedRedButton
                            width="110px"
                            height="2.5rem"
                            text={`선택 삭제 (${selectedDeliveryIds.length})`}
                            onClick={handleDeleteSelected}
                        />
                    </DeleteButtonContainer>
                )}
            </DeliveryViewHeaderContainer>

            <DeliveryContentContainer>
                <DeliveryListTable
                    selectedDeliveryIds={selectedDeliveryIds}
                    setSelectedDeliveryIds={setSelectedDeliveryIds}
                    showDateColumn={isDifferentDates}
                />
                <PageControllerContainer>
                    <PageController basePage={deliveryPage} onPageChange={handleChangePage} />
                </PageControllerContainer>
            </DeliveryContentContainer>

            {deliveryDialogOpen && (
                <DeliveryDialog
                    setIdPayload={setIdPayload}
                    handleClickDialogClose={handleClickDialogClose}
                />
            )}
            {excelDialogOpen && (
                <DeliveryExcelUploadDialog
                    handleClickExcelDialogClose={handleClickExcelDialogClose}
                />
            )}
            {deleteDeliveryAllDialogOpen && (
                <DeleteDeliveryAllDialogContainer
                    handleClickDialogClose={handleClickDeleteDeliveryAllDialogClose}
                />
            )}
        </DeliveryViewContainer>
    );
};

export default DeliveryView;

const DeliveryViewContainer = styled.div`
  width: 93vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
`;

const DeliveryViewTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DeliveryViewTitleLeftContainer = styled.div`
  color: ${theme.palette.primitives.black};
  font-size: 1.25rem;
  font-family: "Pretendard";
  font-style: normal;
  font-weight: 700;
  line-height: 1.5rem;
  padding: 1.5rem 0;
`;
const DeliveryViewTitleRightContainer = styled.div`
  display: flex;
  margin-top: 1rem;
`;

const ContainedGrayButtonContainer = styled.div`
  margin-right: 0.75rem;
`;

const ContainedRedButtonContainer = styled.div`
  margin-right: 0.75rem;
`;

const DeliveryViewHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const PageControllerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const Divider = styled.div`
  width: 1px;
  height: 2.125rem;
  margin: 0 1rem;
  background: ${theme.palette.primitives.gray200};
`;

const SelectorContainer = styled.div`
  margin-right: 0.5rem;
`;

const DeliveryContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MultiSelectorContainer = styled.div`
  margin-left: 0.5rem;
`

const DeleteButtonContainer = styled.div`
    margin-left: 0.5rem;
`;
